import { lazy, Suspense, type ComponentType, type ReactNode } from 'react'
import { createBrowserRouter, Link, Navigate } from 'react-router'
import { AppShell } from '@/components/layout/AppShell'
import { RouteError } from '@/components/layout/RouteError'
import { OperatorRoute, RequireAuth, RequireSuperadmin } from '@/components/layout/guards'
import { LoadingBlock } from '@/components/common'
import { LoginPage } from '@/features/auth/LoginPage'
import type { Permission } from '@/lib/permissions'
import { isStaleBuildError, reloadForUpdate } from '@/lib/updates'

function page<M extends Record<K, ComponentType>, K extends keyof M>(load: () => Promise<M>, name: K) {
  const C = lazy(async () => {
    try {
      return { default: (await load())[name] as ComponentType }
    } catch (e) {
      // An old tab after a deploy: reload into the new build (once).
      if (isStaleBuildError(e) && reloadForUpdate()) return new Promise<never>(() => {})
      throw e
    }
  })
  return (
    <Suspense fallback={<LoadingBlock rows={6} />}>
      <C />
    </Suspense>
  )
}

const op = (el: ReactNode, perm?: Permission) => <OperatorRoute perm={perm}>{el}</OperatorRoute>
const sa = (el: ReactNode) => <RequireSuperadmin>{el}</RequireSuperadmin>

function NotFound() {
  return (
    <div className="py-20 text-center">
      <p className="text-5xl font-semibold">404</p>
      <p className="mt-2 text-text-2">This page doesn't exist.</p>
      <Link to="/" className="mt-4 inline-block underline">
        Go to the dashboard
      </Link>
    </div>
  )
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  {
    path: '/',
    errorElement: <RouteError />,
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: op(page(() => import('@/features/dashboard/DashboardPage'), 'DashboardPage')) },
      { path: 'wallet', element: op(page(() => import('@/features/wallet/WalletPage'), 'WalletPage')) },
      { path: 'live', element: op(page(() => import('@/features/live/LivePage'), 'LivePage')) },
      { path: 'stations', element: op(page(() => import('@/features/stations/StationsPage'), 'StationsPage')) },
      { path: 'stations/:id', element: op(page(() => import('@/features/stations/StationDetailPage'), 'StationDetailPage')) },
      { path: 'chargers', element: op(page(() => import('@/features/chargers/ChargersPage'), 'ChargersPage')) },
      { path: 'chargers/:id', element: op(page(() => import('@/features/chargers/ChargerDetailPage'), 'ChargerDetailPage')) },
      { path: 'commissioning', element: op(page(() => import('@/features/commissioning/CommissioningPage'), 'CommissioningPage'), 'cpo.write') },
      { path: 'commissioning/:id', element: op(page(() => import('@/features/commissioning/CommissioningPage'), 'CommissionWizard'), 'cpo.write') },
      { path: 'fleets', element: op(page(() => import('@/features/fleets/FleetsPage'), 'FleetsPage'), 'cpo.admin') },
      { path: 'fleets/:id', element: op(page(() => import('@/features/fleets/FleetsPage'), 'FleetDetailPage'), 'cpo.admin') },
      { path: 'expenses', element: op(page(() => import('@/features/expenses/ExpensesPage'), 'ExpensesPage'), 'expenses.write') },
      { path: 'daily-report', element: op(page(() => import('@/features/expenses/DailyReportPage'), 'DailyReportPage'), 'expenses.write') },
      { path: 'accounting', element: op(page(() => import('@/features/accounting/AccountingPage'), 'AccountingPage'), 'cpo.admin') },
      { path: 'margin', element: op(page(() => import('@/features/reports/MarginPage'), 'MarginPage'), 'cpo.admin') },
      { path: 'utilisation', element: op(page(() => import('@/features/reports/UtilisationPage'), 'UtilisationPage'), 'analytics.read') },
      { path: 'support', element: op(page(() => import('@/features/support/SupportPage'), 'SupportPage'), 'cpo.write') },
      { path: 'reviews', element: op(page(() => import('@/features/support/ReviewsPage'), 'ReviewsPage'), 'cpo.admin') },
      { path: 'sessions', element: op(page(() => import('@/features/sessions/SessionsPage'), 'SessionsPage')) },
      { path: 'anomalies', element: op(page(() => import('@/features/sessions/AnomaliesPage'), 'AnomaliesPage')) },
      { path: 'pricing', element: op(page(() => import('@/features/pricing/PricingPage'), 'PricingPage'), 'pricing.manage') },
      { path: 'customers', element: op(page(() => import('@/features/customers/CustomersPage'), 'CustomersPage'), 'cpo.admin') },
      { path: 'customers/:id', element: op(page(() => import('@/features/customers/CustomerDetailPage'), 'CustomerDetailPage'), 'cpo.admin') },
      { path: 'cards', element: op(page(() => import('@/features/customers/CardsPage'), 'CardsPage'), 'rfid.manage') },
      { path: 'transactions', element: op(page(() => import('@/features/customers/TransactionsPage'), 'TransactionsPage'), 'credits.manage') },
      { path: 'logs', element: op(page(() => import('@/features/logs/LogsPage'), 'LogsPage')) },
      { path: 'reports', element: op(page(() => import('@/features/reports/ReportsPage'), 'ReportsPage'), 'analytics.read') },
      { path: 'settlements', element: op(page(() => import('@/features/reports/SettlementsPage'), 'SettlementsPage'), 'cpo.admin') },
      { path: 'electricity', element: op(page(() => import('@/features/electricity/ElectricityPage'), 'ElectricityPage'), 'electricity.manage') },
      // The old address of the NEA page.
      { path: 'nea', element: <Navigate to="/electricity" replace /> },
      { path: 'uptime', element: op(page(() => import('@/features/upcoming/UptimePage'), 'UptimePage'), 'analytics.read') },
      { path: 'team', element: op(page(() => import('@/features/team/TeamPage'), 'TeamPage'), 'cpo.admin') },
      { path: 'settings', element: op(page(() => import('@/features/settings/SettingsPage'), 'SettingsPage'), 'cpo.admin') },
      { path: 'reservations', element: op(page(() => import('@/features/upcoming/ReservationsPage'), 'ReservationsPage')) },
      { path: 'load', element: op(page(() => import('@/features/upcoming/LoadPage'), 'LoadPage'), 'cpo.write') },
      { path: 'payments', element: op(page(() => import('@/features/upcoming/PaymentsPage'), 'PaymentsPage'), 'cpo.admin') },
      { path: 'promotions', element: op(page(() => import('@/features/upcoming/PromotionsPage'), 'PromotionsPage'), 'cpo.admin') },
      { path: 'maintenance', element: op(page(() => import('@/features/upcoming/MaintenancePage'), 'MaintenancePage')) },
      { path: 'alerts', element: op(page(() => import('@/features/upcoming/AlertsPage'), 'AlertsPage'), 'cpo.write') },
      { path: 'audit', element: op(page(() => import('@/features/audit/AuditPage'), 'AuditPage'), 'cpo.admin') },
      { path: 'profile', element: page(() => import('@/features/settings/ProfilePage'), 'ProfilePage') },
      // Superadmin
      { path: 'admin', element: sa(page(() => import('@/features/admin/OverviewPage'), 'OverviewPage')) },
      { path: 'admin/operators', element: sa(page(() => import('@/features/admin/OperatorsPage'), 'OperatorsPage')) },
      { path: 'admin/users', element: sa(page(() => import('@/features/admin/UsersPage'), 'UsersPage')) },
      { path: 'admin/chargers', element: sa(page(() => import('@/features/admin/AllChargersPage'), 'AllChargersPage')) },
      { path: 'admin/sessions', element: sa(page(() => import('@/features/admin/AllSessionsPage'), 'AllSessionsPage')) },
      { path: 'admin/super-cards', element: sa(page(() => import('@/features/admin/SuperCardsPage'), 'SuperCardsPage')) },
      { path: 'admin/topups', element: sa(page(() => import('@/features/admin/AdminTopUpsPage'), 'AdminTopUpsPage')) },
      { path: 'admin/transactions', element: sa(page(() => import('@/features/admin/AdminTransactionsPage'), 'AdminTransactionsPage')) },
      { path: 'admin/nea-tariff', element: sa(page(() => import('@/features/admin/NeaTariffPage'), 'NeaTariffPage')) },
      { path: 'admin/settlements', element: sa(page(() => import('@/features/admin/AdminSettlementsPage'), 'AdminSettlementsPage')) },
      { path: 'admin/payouts', element: sa(page(() => import('@/features/accounting/AccountingPage'), 'AdminPayoutsPage')) },
      { path: 'admin/support', element: sa(page(() => import('@/features/support/SupportPage'), 'AdminSupportPage')) },
      { path: 'admin/push', element: sa(page(() => import('@/features/admin/PushPage'), 'PushPage')) },
      { path: 'admin/carousel', element: sa(page(() => import('@/features/admin/CarouselPage'), 'CarouselPage')) },
      { path: 'admin/audit', element: sa(page(() => import('@/features/audit/AuditPage'), 'AdminAuditPage')) },
      { path: 'admin/settings', element: sa(page(() => import('@/features/admin/PlatformSettingsPage'), 'PlatformSettingsPage')) },
      { path: '*', element: <NotFound /> },
    ],
  },
])
